import { useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updateActivity } from '../redux/slices/authSlice';

const useIdleTimer = () => {
  const dispatch = useDispatch();
  const { isAuthenticated } = useSelector((state) => state.auth);
  const lastActivityRef = useRef(Date.now());
  const throttleTimeoutRef = useRef(null);

  // Throttled activity update - only update once per minute to avoid excessive updates
  const handleActivity = useCallback(() => {
    if (!isAuthenticated) return;

    const now = Date.now();
    const timeSinceLastUpdate = now - lastActivityRef.current;

    // Only update if more than 1 minute has passed since last update
    if (timeSinceLastUpdate > 60000) {
      // Clear any pending throttled update
      if (throttleTimeoutRef.current) {
        clearTimeout(throttleTimeoutRef.current);
        throttleTimeoutRef.current = null;
      }

      lastActivityRef.current = now;
      dispatch(updateActivity());
    } else if (!throttleTimeoutRef.current) {
      // Schedule an update for 1 minute from last activity
      throttleTimeoutRef.current = setTimeout(() => {
        lastActivityRef.current = Date.now();
        dispatch(updateActivity());
        throttleTimeoutRef.current = null;
      }, 60000 - timeSinceLastUpdate);
    }
  }, [isAuthenticated, dispatch]);

  useEffect(() => {
    if (!isAuthenticated) return;

    // Activity events to monitor
    const events = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click',
    ];

    // Add event listeners
    events.forEach((event) => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    // Cleanup
    return () => {
      events.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
      
      if (throttleTimeoutRef.current) {
        clearTimeout(throttleTimeoutRef.current);
      }
    };
  }, [isAuthenticated, handleActivity]);
};

export default useIdleTimer;
